import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Eye, Loader2, Plus, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ownerAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const STATUS_OPTIONS = [
  '',
  'DRAFT',
  'INCOMPLETE',
  'READY_FOR_REVIEW',
  'PUBLISHED',
  'PAUSED',
];

const STATUS_STYLES = {
  DRAFT: 'bg-slate-500/20 text-slate-700 border-slate-300',
  INCOMPLETE: 'bg-yellow-500/20 text-yellow-700 border-yellow-300',
  READY_FOR_REVIEW: 'bg-amber-500/20 text-amber-700 border-amber-300',
  PUBLISHED: 'bg-emerald-500/20 text-emerald-700 border-emerald-300',
  PAUSED: 'bg-orange-500/20 text-orange-700 border-orange-300',
};

export default function OwnerListingsPage() {
  const navigate = useNavigate();
  const { isOwner, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!authLoading && !isOwner) {
      navigate('/login?redirect=/owner/listings');
    }
  }, [authLoading, isOwner, navigate]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const res = await ownerAPI.getListings();
      setListings(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load owner listings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) loadListings();
  }, [isOwner]);

  const visibleListings = useMemo(() => {
    const term = search.trim().toLowerCase();
    return listings.filter((listing) => {
      if (statusFilter && listing.status !== statusFilter) return false;
      if (!term) return true;
      return [listing.title, listing.location_name, listing.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [listings, statusFilter, search]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="owner-listings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-800">
              {t('owner.your_listings', 'YOUR LISTINGS').toUpperCase()}
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              View every listing, including draft, pending-review, published, and paused inventory.
            </p>
          </div>
          <Button
            onClick={() => navigate('/owner/listings/new')}
            className="bg-military-gold hover:bg-military-gold/90 text-slate-950 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('owner.add_new_listing', 'Add New Listing')}
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, location, or status..."
                className="pl-10 bg-white border-slate-300 text-slate-800"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-700 text-sm"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status || 'all'} value={status}>
                  {status ? status.replaceAll('_', ' ') : 'All Statuses'}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={loadListings} className="border-slate-300 text-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {visibleListings.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No listings match this view.</p>
              <p className="text-slate-500 text-sm mt-1">Clear filters or create a new listing.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">Listing</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">Completion</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">Price</th>
                    <th className="text-left py-3 px-4 text-slate-600 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleListings.map((listing) => (
                    <tr key={listing.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 bg-slate-100 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0">
                            {listing.hero_photo_url ? (
                              <img src={listing.hero_photo_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Building2 className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-slate-800 font-medium">{listing.title}</p>
                            <p className="text-xs text-slate-500">{listing.location_name || 'Unknown location'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={`${STATUS_STYLES[listing.status] || 'bg-slate-100 text-slate-700 border-slate-300'} border text-xs`}>
                          {(listing.status || '').replaceAll('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-700 text-sm">{listing.completion_percent ?? 0}%</span>
                        <p className="text-xs text-slate-500">{listing.photo_count ?? listing.photos?.length ?? 0} photos</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-military-gold font-bold">€{listing.base_amount || 0}</span>
                        <span className="text-slate-500 text-xs">/mo</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/owner/listings/${listing.id}/edit`)}
                            className="border-slate-300 text-slate-700"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/listings/${listing.id}`)}
                            className="text-signal-blue"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Preview
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
